import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import {
  Flex,
  Table,
  Button,
  Divider,
  TableRow,
  Checkbox,
  TableBody,
  TableHead,
  TableCell,
  Pagination,
  ActionMenu,
  FixedFooter,
  TableActions,
  TableHeadCell,
  FixedFooterContainer,
  StretchPanel as Panel,
  EmptyStatePlaceholder,
  OrderableTableHeadCell,
} from '@ubisend/pulse-components';
import { PermissionFilter } from '@ubisend/pulse-auth';

import SubscriberFooter from './SubscriberFooter';

function formatDate(date, getTime) {
  return date ? `${new Date(date).toDateString()} (${getTime(date)})` : 'N/A';
}

export default function SubscriberTable({
  order,
  query,
  getTime,
  pagination,
  handleDelete,
  allItemsSelected,
  handleExportData,
  selectedSubscribers,
  handleExportTranscript,
  handleSubscriberSelect,
  setSelectedSubscribers,
  handleSelectingAllItems
}) {
  return (
    <Flex col>
      <Panel>
        {query.showNoResultsMessage && (
          <EmptyStatePlaceholder
            type="conversations"
            heading="Nothing to show yet"
            text="When users start engaging with your automation, conversation logs will appear here."
            helpLink="/docs"
            helpText="Learn more about conversations."
          />
        )}

        {query.showTable && (
          <>
            <Table loading={query.isLoading} loadingColumns={6}>
              <TableHead>
                <TableRow>
                  <TableHeadCell>
                    <Checkbox
                      checked={allItemsSelected}
                      onChange={handleSelectingAllItems}
                      aria-label="select-all-conversations"
                    />
                  </TableHeadCell>
                  <OrderableTableHeadCell row={{ label: 'Subscriber ID', sort: 'id' }} {...order.props} />
                  <OrderableTableHeadCell row={{ label: 'Last message sent at', sort: 'last_active_at' }} {...order.props} />
                  <OrderableTableHeadCell row={{ label: 'Conversation Started on', sort: 'created_at' }} {...order.props} />
                  <OrderableTableHeadCell row={{ label: 'Channel', sort: null }} {...order.props} />
                  <OrderableTableHeadCell row={{ label: 'Messages sent', sort: 'messages_count' }} {...order.props} />
                </TableRow>
              </TableHead>

              {query.isSuccess && (
                <TableBody>
                  {query.data.data.map((subscriber, key) => (
                    <TableRow key={subscriber.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedSubscribers.includes(subscriber.id)}
                          onChange={() => handleSubscriberSelect(subscriber.id)}
                          aria-label={`batch-select-${key + 1}`}
                        />
                      </TableCell>
                      <TableCell>{subscriber.id}</TableCell>
                      <TableCell>{formatDate(subscriber.last_active_at, getTime)}</TableCell>
                      <TableCell>{formatDate(subscriber.created_at, getTime)}</TableCell>
                      <TableCell>{subscriber.channel?.driver?.name || 'N/A'}</TableCell>
                      <TableCell>{subscriber.message_count}</TableCell>
                      <TableActions>
                        <ActionMenu
                          position={ActionMenu.POSITIONS.LEFT}
                          buttonProps={{
                            'aria-label': `toggle-conversation-menu-${key}`,
                            onMouseDown: (e) => e.stopPropagation(),
                            onMouseUp: (e) => e.stopPropagation(),
                          }}
                        >
                          <PermissionFilter can="view messages">
                            <Button as={Link} variant="inline" to={`/conversations/${subscriber.id}`} icon="eye">
                              View
                            </Button>
                            <Divider mtNone mbNone />
                            <Button variant="inline" icon="download" onClick={() => handleExportData(subscriber.id)}>
                              Export Data
                            </Button>
                            <Divider mtNone mbNone />
                            <Button variant="inline" icon="download" onClick={() => handleExportTranscript(subscriber.id)}>
                              Export Transcript
                            </Button>
                            <Divider mtNone mbNone />
                            <Button variant="inline" colour="danger" icon="trash" onClick={() => handleDelete(subscriber.id)}>
                              Delete
                            </Button>
                          </PermissionFilter>
                        </ActionMenu>
                      </TableActions>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>

            {query.showPagination && <Pagination pagination={query.data.meta} {...pagination.props} />}
          </>
        )}
      </Panel>

      {selectedSubscribers.length > 0 && (
        <FixedFooterContainer>
          <FixedFooter>
            <SubscriberFooter
              selectedSubscribers={selectedSubscribers}
              handleMassDataExport={handleExportData}
              handleMassTranscriptExport={handleExportTranscript}
              handleMassDelete={handleDelete}
              handleClear={() => setSelectedSubscribers([])}
            />
          </FixedFooter>
        </FixedFooterContainer>
      )}
    </Flex>
  );
}

SubscriberTable.propTypes = {
  order: PropTypes.object.isRequired,
  query: PropTypes.shape({
    showNoResultsMessage: PropTypes.bool,
    showTable: PropTypes.bool,
    isLoading: PropTypes.bool,
    isSuccess: PropTypes.bool,
    showPagination: PropTypes.bool,
    data: PropTypes.shape({
      data: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          last_active_at: PropTypes.string,
          created_at: PropTypes.string.isRequired,
          channel: PropTypes.shape({
            driver: PropTypes.shape({
              name: PropTypes.string,
            }),
          }),
          message_count: PropTypes.number,
        })
      ),
      meta: PropTypes.object,
    }),
  }).isRequired,
  getTime: PropTypes.func.isRequired,
  pagination: PropTypes.object.isRequired,
  selectedSubscribers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  allItemsSelected: PropTypes.bool.isRequired,
  handleSubscriberSelect: PropTypes.func.isRequired,
  handleSelectingAllItems: PropTypes.func.isRequired,
  handleExportData: PropTypes.func.isRequired,
  handleExportTranscript: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  setSelectedSubscribers: PropTypes.func.isRequired,
};